import { BillingClosureBadges } from "../../components/BillingClosureBadges";
import { billingClosureState } from "../../utils/billingClosureState";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GridToolbarContainer, GridToolbarQuickFilter, itIT } from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import { MdCheck, MdClose, MdNotes } from "react-icons/md";
import { toast } from "react-toastify";
import api from "../../api/api";
import { CustomFooterPersonalized } from "../../components/DataGrid/CustomFooterPersonalized";
import { knowAcces } from "../../utils/utils";
import { formatItalianNumber } from "../../utils/formatItalianNumber";
import useCedoliniRealtime from "../../hooks/useCedoliniRealtime";

const Metric = ({ label, value, zucchetti, iipay, bpoint, soloBpoint = false, modifiedValue, modifiedZucchetti, modifiedIipay, modifiedBpoint, tone }) => (
  <div className={`rounded-lg border px-4 py-3 ${tone}`}>
    <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</div>
    <div className="mt-1 flex items-baseline gap-2 text-2xl font-bold">
      <span>{formatItalianNumber(value, { maximumFractionDigits: 0 })}</span>
      {modifiedValue !== undefined && Number(modifiedValue) !== Number(value) && <span
        title="Totale risultante dai valori modificati"
        className="text-base font-semibold opacity-70">({formatItalianNumber(modifiedValue, { maximumFractionDigits: 0 })})</span>}
    </div>
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 border-t border-current border-opacity-10 pt-2 text-xs font-semibold">
      {!soloBpoint && <><span>Zucchetti: {formatItalianNumber(zucchetti, { maximumFractionDigits: 0 })}{modifiedZucchetti !== undefined && Number(modifiedZucchetti) !== Number(zucchetti) ? ` (${formatItalianNumber(modifiedZucchetti, { maximumFractionDigits: 0 })})` : ""}</span>
      <span>iiPay: {formatItalianNumber(iipay, { maximumFractionDigits: 0 })}{modifiedIipay !== undefined && Number(modifiedIipay) !== Number(iipay) ? ` (${formatItalianNumber(modifiedIipay, { maximumFractionDigits: 0 })})` : ""}</span></>}
      {soloBpoint && <span>BPoint: {formatItalianNumber(bpoint, { maximumFractionDigits: 0 })}{modifiedBpoint !== undefined && Number(modifiedBpoint) !== Number(bpoint) ? ` (${formatItalianNumber(modifiedBpoint, { maximumFractionDigits: 0 })})` : ""}</span>}
    </div>
  </div>
);

const defaultNoteTypes = {
  nota_1: {
    titolo: "Nota 1",
    testo: "NO 13/14me se spalmate in bp",
    descrizione: "Non inserire il numero di 13/14me erogate mensilmente in busta paga con rateo mensile. Inserire invece il numero di 13/14me elaborate ed erogate in un'unica soluzione, nel cedolino della mensilità ordinaria o con cedolino a parte. Se sono presenti soltanto 13/14me erogate mensilmente in busta, riportare il valore 0.",
  },
  nota_2: {
    titolo: "Nota 2",
    testo: "13-14ma x dip in forza",
    descrizione: "Inserire il numero di dipendenti con 13/14ma erogata mensilmente in busta, quindi con rateo mensile, in forza nell'ultima mensilità elaborata: novembre per la 13ª e maggio per la 14ª. Aggiungere le eventuali 13/14me elaborate ed erogate in un'unica soluzione, nel cedolino ordinario o con cedolino a parte.",
  },
};

const NoteDot = ({ title, text, className }) => (
  <Tooltip title={<div className="max-w-xs text-sm"><div className="font-semibold">{title}</div><div>{text}</div></div>} arrow placement="top">
    <span aria-label={`${title} - ${text}`} className={className} />
  </Tooltip>
);

const sanitizeFormattedHtml = (value = "") => {
  if (typeof DOMParser === "undefined") return value;
  const documentHtml = new DOMParser().parseFromString(value, "text/html");
  const allowed = new Set(["B", "STRONG", "I", "EM", "U", "P", "BR", "UL", "OL", "LI", "DIV"]);
  [...documentHtml.body.querySelectorAll("*")].forEach((element) => {
    if (!allowed.has(element.tagName)) element.replaceWith(...element.childNodes);
    else [...element.attributes].forEach((attribute) => element.removeAttribute(attribute.name));
  });
  return documentHtml.body.innerHTML;
};

const asFormattedHtml = (value = "") => {
  if (/<[a-z][\s\S]*>/i.test(value)) return sanitizeFormattedHtml(value);
  const escaped = value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped.replace(/\n/g, "<br>");
};

const RichTextEditor = ({ value, onChange, disabled }) => {
  const editorRef = useRef(null);
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== asFormattedHtml(value)) {
      editorRef.current.innerHTML = asFormattedHtml(value);
    }
  }, [value]);

  const format = (command, argument = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, argument);
    onChange(sanitizeFormattedHtml(editorRef.current?.innerHTML || ""));
  };
  const tool = (label, command, className = "") => <button type="button" disabled={disabled}
    onMouseDown={(event) => { event.preventDefault(); format(command); }}
    className={`min-w-9 rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-100 disabled:opacity-50 ${className}`}>{label}</button>;

  return <div className="overflow-hidden rounded-lg border border-slate-300 focus-within:border-blue-400">
    <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 p-2">
      {tool("G", "bold", "font-bold")}{tool("C", "italic", "italic")}{tool("S", "underline", "underline")}
      <span className="mx-1 border-l border-slate-300" />
      {tool("• Elenco", "insertUnorderedList")}{tool("1. Elenco", "insertOrderedList")}
      {tool("Rimuovi formato", "removeFormat")}
    </div>
    <div ref={editorRef} contentEditable={!disabled} suppressContentEditableWarning
      onInput={(event) => onChange(sanitizeFormattedHtml(event.currentTarget.innerHTML))}
      data-placeholder="Inserisci qui le linee guida operative..."
      className="min-h-48 max-h-[30vh] overflow-y-auto p-4 text-sm leading-6 text-slate-800 outline-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)]" />
  </div>;
};

export const Cedolini = ({ data, periodo, tuttiTeam = false, onTuttiTeamChange, showTuttiTeam = false, roleReadOnly = false, onRealtimeChange }) => {
  const [anno, mese] = data.split("-").map(Number);
  const [rows, setRows] = useState([]);
  const [tipoAggiuntivo, setTipoAggiuntivo] = useState(null);
  const [periodoDati, setPeriodoDati] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingCode, setSavingCode] = useState("");
  const [soloCessati, setSoloCessati] = useState(false);
  const [soloForzati, setSoloForzati] = useState(false);
  const [lineeGuidaOpen, setLineeGuidaOpen] = useState(false);
  const [lineeGuida, setLineeGuida] = useState("");
  const [lineeGuidaLoading, setLineeGuidaLoading] = useState(false);
  const [lineeGuidaPreview, setLineeGuidaPreview] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSearch, setNoteSearch] = useState("");

  const selectZeroOnFocus = (event) => {
    if (event.currentTarget.value !== "" && Number(event.currentTarget.value) === 0) {
      event.currentTarget.select();
    }
  };
  const [noteTypes, setNoteTypes] = useState(defaultNoteTypes);
  const [noteRows, setNoteRows] = useState([]);
  const [selectedClients, setSelectedClients] = useState({});
  const [notaInformativa, setNotaInformativa] = useState(null);
  const [notaInformativaSaving, setNotaInformativaSaving] = useState(false);
  const [tipologieFiltro, setTipologieFiltro] = useState(new Set());
  const [sottotab, setSottotab] = useState("zi");
  const [mostraFondiSync, setMostraFondiSync] = useState(false);
  const isAdmin = knowAcces(["parc_admin"]);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const response = await api.get("/billing/cedolini-riepilogo", { params: { anno, mese, tutti: tuttiTeam ? "1" : "0" }, signal });
      setRows(response.data.rows || []);
      setTipoAggiuntivo(response.data.tipo_mensilita_aggiuntiva || null);
      setPeriodoDati(response.data.periodo_dati || null);
      setMostraFondiSync(response.data.mostra_fondi_sync === true);
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      toast.error(error.response?.data?.message || "Errore caricamento cedolini");
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [anno, mese, tuttiTeam]);

  useEffect(() => {
    const controller = new AbortController(); load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useCedoliniRealtime({
    anno,
    mese,
    onChange: () => {
      load();
      onRealtimeChange?.();
    },
  });

  const saveAggiuntiva = async (row, value) => {
    const isCleared = value === "";
    const numero = isCleared ? null : Number(value);
    if ((!isCleared && (!Number.isInteger(numero) || numero < 0))
      || (isCleared && row.mensilita_aggiuntiva == null)
      || (!isCleared && row.mensilita_aggiuntiva != null && numero === Number(row.mensilita_aggiuntiva))) return;
    setSavingCode(row.codice_univoco_azienda);
    try {
      await api.put(`/billing/cedolini-riepilogo/${row.codice_univoco_azienda}/mensilita-aggiuntiva`, {
        anno, mese, numero_cedolini: numero, tutti_team: tuttiTeam,
      });
      setRows((current) => current.map((item) => item.codice_univoco_azienda === row.codice_univoco_azienda
        ? { ...item, mensilita_aggiuntiva: numero } : item));
      toast.success(`${tipoAggiuntivo === "quattordicesima" ? "14ª" : "13ª"} ${isCleared ? "rimossa" : "salvata"} per ${row.ragione_sociale}`);
    } catch (error) { toast.error(error.response?.data?.message || "Errore salvataggio mensilità"); }
    finally { setSavingCode(""); }
  };

  const saveFondi = async (row, value) => {
    const isCleared = value === "";
    const numero = isCleared ? null : Number(value);
    if ((!isCleared && (!Number.isInteger(numero) || numero < 0))
      || (isCleared && row.numero_fondi == null)
      || (!isCleared && row.numero_fondi != null && numero === Number(row.numero_fondi))) return;
    setSavingCode(`${row.codice_univoco_azienda}-fondi`);
    try {
      await api.put(`/billing/cedolini-riepilogo/${row.codice_univoco_azienda}/fondi`, {
        anno, mese, numero_fondi: numero, tutti_team: tuttiTeam,
      });
      setRows((current) => current.map((item) => item.codice_univoco_azienda === row.codice_univoco_azienda
        ? { ...item, numero_fondi: numero, stato_fondi: item.fondi_obbligatori ? (numero == null ? "Obbl." : "Compilato") : null } : item));
      toast.success(`Numero fondi ${isCleared ? "rimosso" : "salvato"} per ${row.ragione_sociale}`);
    } catch (error) { toast.error(error.response?.data?.message || "Errore salvataggio numero fondi"); }
    finally { setSavingCode(""); }
  };

  const isRowClosed = (row) => (row.tipologia_fatturazione === "B" && periodo?.bpoint_invoice_closed)
    || (row.tipologia_fatturazione !== "B" && periodo?.stato === "chiuso")
    || (!isAdmin && row.tipologia_fatturazione === "B" && Boolean(periodo?.chiuso_sez0))
    || (!isAdmin && row.tipologia_fatturazione === "Z" && Boolean(periodo?.chiuso_sez1))
    || (!isAdmin && row.tipologia_fatturazione === "I" && Boolean(periodo?.chiuso_sez2));

  const saveForzatura = async (row, campo, value) => {
    const numero = Number(value);
    if (!Number.isInteger(numero) || numero < 0 || numero === Number(row[campo] || 0)) return;
    setSavingCode(`${row.codice_univoco_azienda}-${campo}`);
    try {
      await api.put(`/billing/cedolini-riepilogo/${row.codice_univoco_azienda}/forzatura`, { anno, mese, campo, valore: numero, tutti_team: tuttiTeam });
      toast.success("Valore forzato: in attesa di approvazione admin");
      await load();
    } catch (error) { toast.error(error.response?.data?.message || "Errore forzatura cedolini"); }
    finally { setSavingCode(""); }
  };

  const decideForzatura = async (id, action) => {
    try {
      await api.post(`/billing/cedolini-riepilogo/forzature/${id}/${action}`);
      toast.success(action === "approve" ? "Forzatura approvata" : "Forzatura respinta");
      await load();
    } catch (error) { toast.error(error.response?.data?.message || "Errore approvazione forzatura"); }
  };

  const deleteForzaturaDraft = async (id) => {
    try {
      await api.delete(`/billing/cedolini-riepilogo/forzature/${id}`);
      toast.success("Bozza eliminata e valore originale ripristinato");
      await load();
    } catch (error) { toast.error(error.response?.data?.message || "Errore eliminazione bozza"); }
  };

  const saveNotaInformativa = async () => {
    if (!notaInformativa) return;
    setNotaInformativaSaving(true);
    try {
      const testo = notaInformativa.testo.trim();
      await api.put(`/billing/cedolini-riepilogo/${notaInformativa.codice}/nota-informativa`, { testo });
      setRows((current) => current.map((row) => row.codice_univoco_azienda === notaInformativa.codice
        ? { ...row, nota_informativa: testo || null } : row));
      setNotaInformativa(null);
      toast.success(testo ? "Nota informativa salvata" : "Nota informativa eliminata");
    } catch (error) { toast.error(error.response?.data?.message || "Errore salvataggio nota informativa"); }
    finally { setNotaInformativaSaving(false); }
  };

  const openLineeGuida = async () => {
    setLineeGuidaOpen(true); setLineeGuidaLoading(true); setLineeGuidaPreview(false);
    try {
      const bpoint = sottotab === "bpoint";
      const [lineeGuidaResponse, noteResponse] = await Promise.all([
        api.get(`/billing/cedolini-riepilogo/${bpoint ? "linee-guida-bpoint" : "linee-guida"}/${tipoAggiuntivo}`),
        bpoint ? Promise.resolve({ data: {} }) : api.get("/billing/cedolini-riepilogo/note-mensilita"),
      ]);
      setLineeGuida(lineeGuidaResponse.data?.contenuto || "");
      setNoteTypes({ ...defaultNoteTypes, ...(noteResponse.data?.tipi || {}) });
    } catch (error) { toast.error(error.response?.data?.message || "Errore caricamento linee guida"); }
    finally { setLineeGuidaLoading(false); }
  };

  const saveLineeGuida = async () => {
    setLineeGuidaLoading(true);
    try {
      await Promise.all([
        api.put(`/billing/cedolini-riepilogo/${sottotab === "bpoint" ? "linee-guida-bpoint" : "linee-guida"}/${tipoAggiuntivo}`, { contenuto: lineeGuida }),
        isAdmin && sottotab !== "bpoint" ? api.put("/billing/cedolini-riepilogo/note-mensilita", { tipi: noteTypes }) : Promise.resolve(),
      ]);
      toast.success("Linee guida salvate"); setLineeGuidaOpen(false);
    } catch (error) { toast.error(error.response?.data?.message || "Errore salvataggio linee guida"); }
    finally { setLineeGuidaLoading(false); }
  };

  const openGestioneNote = async () => {
    setNoteOpen(true);
    setNoteLoading(true);
    try {
      const response = await api.get("/billing/cedolini-riepilogo/note-mensilita");
      setNoteTypes({ ...defaultNoteTypes, ...(response.data?.tipi || {}) });
      setNoteRows(response.data?.clienti || []);
      setSelectedClients({});
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore caricamento note clienti");
    } finally {
      setNoteLoading(false);
    }
  };

  const updateNoteType = (tipo, field, value) => {
    setNoteTypes((current) => ({
      ...current,
      [tipo]: {
        ...current[tipo],
        [field]: value,
      },
    }));
  };

  const toggleClientSelection = (codice) => {
    setSelectedClients((current) => ({ ...current, [codice]: !current[codice] }));
  };

  const setClientNote = (codice, tipo, value) => {
    setNoteRows((current) => current.map((row) => row.codice_univoco_azienda === codice
      ? { ...row, [tipo]: value }
      : row));
  };

  const filteredNoteRows = useMemo(() => {
    const needle = noteSearch.trim().toLowerCase();
    if (!needle) return noteRows;
    return noteRows.filter((row) => (`${row.ragione_sociale} ${row.codice_univoco_azienda} ${row.tipologia_fatturazione}`).toLowerCase().includes(needle));
  }, [noteRows, noteSearch]);

  const selectedCodes = useMemo(() => Object.entries(selectedClients)
    .filter(([, checked]) => checked)
    .map(([codice]) => codice), [selectedClients]);

  const applyMassNote = (tipo, value) => {
    if (!selectedCodes.length) return;
    setNoteRows((current) => current.map((row) => selectedCodes.includes(row.codice_univoco_azienda)
      ? { ...row, [tipo]: value }
      : row));
  };

  const clearSelectedNotes = () => {
    if (!selectedCodes.length) return;
    setNoteRows((current) => current.map((row) => selectedCodes.includes(row.codice_univoco_azienda)
      ? { ...row, nota_1: false, nota_2: false }
      : row));
  };

  const saveNoteConfiguration = async () => {
    setNoteSaving(true);
    try {
      await api.put("/billing/cedolini-riepilogo/note-mensilita", {
        tipi: noteTypes,
        assegnazioni: noteRows.map((row) => ({
          codice_univoco_azienda: row.codice_univoco_azienda,
          nota_1: Boolean(row.nota_1),
          nota_2: Boolean(row.nota_2),
        })),
      });
      toast.success("Note clienti salvate");
      setNoteOpen(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore salvataggio note clienti");
    } finally {
      setNoteSaving(false);
    }
  };

  const editableNumberColumn = (field, headerName, width) => ({
    field, headerName, type: "number", width, sortable: true, align: "center", headerAlign: "center",
    renderCell: ({ row }) => {
      const modifica = row.forzature?.[field];
      const disabled = roleReadOnly || isRowClosed(row) || savingCode === `${row.codice_univoco_azienda}-${field}`
        || (field === "cedolini_espatriati" && row.tipologia_fatturazione !== "I")
        || (["cedolini_intermittenti", "cedolini_somministrati"].includes(field) && row.tipologia_fatturazione !== "Z");
      if ((field === "cedolini_espatriati" && row.tipologia_fatturazione !== "I")) return <span className="block w-full text-center text-gray-300">—</span>;
      const pendente = modifica && ["bozza", "forzata"].includes(modifica.stato);
      const canDeleteDraft = !roleReadOnly && modifica?.stato === "bozza"
        && modifica.modificato_da === localStorage.getItem("username");
      const valoreOriginale = formatItalianNumber(modifica?.valore_originale ?? row[field] ?? 0, { maximumFractionDigits: 0 });
      return <div className="relative h-full w-full">
        <input type="number" min="0" step="1" defaultValue={Number(row[field] || 0)}
          key={`${row.codice_univoco_azienda}-${field}-${row[field]}`} disabled={disabled}
          onFocus={selectZeroOnFocus}
          onBlur={(event) => saveForzatura(row, field, event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }}
          className={`absolute left-1/2 top-1/2 w-16 -translate-x-1/2 -translate-y-1/2 rounded border px-1.5 py-1 text-right ${modifica ? "border-amber-400 bg-amber-50 font-semibold text-amber-800" : "border-gray-200 bg-white"} disabled:bg-gray-100`} />
        <div className="absolute right-[calc(50%+38px)] top-1/2 -translate-y-1/2 whitespace-nowrap">{modifica && <span title={pendente ? "Forzatura da approvare" : "Forzatura approvata"}
          className={`inline-block rounded px-1 py-0.5 text-[10px] font-bold ${pendente ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`}>
          ORIGINALE: {valoreOriginale}
        </span>}</div>
        <div className="absolute left-[calc(50%+38px)] top-1/2 flex -translate-y-1/2 items-center gap-1">{isAdmin && pendente && <>
          <button type="button" title="Approva forzatura" aria-label="Approva forzatura"
            onClick={() => decideForzatura(modifica.id, "approve")}
            className="flex h-6 w-6 items-center justify-center rounded-full border border-green-300 bg-green-50 text-green-700 shadow-sm transition hover:bg-green-100">
            <MdCheck size={16} />
          </button>
          <button type="button" title="Respingi forzatura" aria-label="Respingi forzatura"
            onClick={() => decideForzatura(modifica.id, "reject")}
            className="flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-50 text-red-600 shadow-sm transition hover:bg-red-100">
            <MdClose size={16} />
          </button>
        </>}{canDeleteDraft && <button type="button" title="Elimina bozza e ripristina il valore originale" aria-label="Elimina bozza"
          onClick={() => deleteForzaturaDraft(modifica.id)}
          className="flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-50 text-red-600 shadow-sm transition hover:bg-red-100">
          <MdClose size={16} />
        </button>}</div>
      </div>;
    },
  });

  const rowsPerTipologia = useMemo(() => {
    const rowsSezione = rows.filter((row) => sottotab === "bpoint"
      ? row.tipologia_fatturazione === "B"
      : ["Z", "I"].includes(row.tipologia_fatturazione));
    if (sottotab === "bpoint" || tipologieFiltro.size === 0) return rowsSezione;
    return rowsSezione.filter((row) => tipologieFiltro.has(String(row.tipologia_fatturazione || "").toUpperCase()));
  }, [rows, sottotab, tipologieFiltro]);

  const totals = useMemo(() => rowsPerTipologia.reduce((sum, row) => {
    const tipo = row.tipologia_fatturazione === "B" ? "b" : row.tipologia_fatturazione === "I" ? "i" : "z";
    const campi = {
      ordinari: "cedolini_ordinari",
      dirigenti: "cedolini_dirigenti",
      edili: "cedolini_edili",
      intermittenti: "cedolini_intermittenti",
      somministrati: "cedolini_somministrati",
      espatriati: "cedolini_espatriati",
      aggiuntiva: "mensilita_aggiuntiva",
    };
    Object.entries(campi).forEach(([totale, campo]) => {
      const valoreModificato = Number(row[campo] || 0);
      const valoreOriginale = Number(row.forzature?.[campo]?.valore_originale ?? valoreModificato);
      sum[totale] += valoreOriginale;
      sum[`${totale}_${tipo}`] += valoreOriginale;
      sum[`${totale}_modificato`] += valoreModificato;
      sum[`${totale}_${tipo}_modificato`] += valoreModificato;
    });
    // Gli ordinari iiPay arrivano già al netto degli espatriati: ogni categoria
    // concorre una sola volta al totale cedolini. Le mensilità aggiuntive
    // restano invece un conteggio separato e non entrano mai nel totale.
    const campiTotale = row.tipologia_fatturazione === "B"
      ? ["cedolini_ordinari"]
      : ["cedolini_ordinari", "cedolini_intermittenti", "cedolini_somministrati", "cedolini_espatriati"];
    const totaleOriginale = campiTotale.reduce((total, campo) => total + Number(row.forzature?.[campo]?.valore_originale ?? row[campo] ?? 0), 0);
    const totaleModificato = campiTotale.reduce((total, campo) => total + Number(row[campo] || 0), 0);
    sum.totale += totaleOriginale;
    sum[`totale_${tipo}`] += totaleOriginale;
    sum.totale_modificato += totaleModificato;
    sum[`totale_${tipo}_modificato`] += totaleModificato;
    return sum;
  }, {
    ...Object.fromEntries(["totale", "ordinari", "dirigenti", "edili", "intermittenti", "somministrati", "espatriati", "aggiuntiva"]
      .flatMap((campo) => [campo, `${campo}_z`, `${campo}_i`, `${campo}_b`, `${campo}_modificato`, `${campo}_z_modificato`, `${campo}_i_modificato`, `${campo}_b_modificato`])
      .map((campo) => [campo, 0])),
  }), [rowsPerTipologia]);

  const tipoColumn = { field: "tipologia_fatturazione", headerName: "Tipo", width: 65,
      renderCell: ({ value }) => <span className={`rounded px-2 py-0.5 text-xs font-bold ${value === "B" ? "bg-purple-100 text-purple-700" : value === "I" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
        {value}
      </span> };
  const commonColumns = [
    { field: "ragione_sociale", headerName: "Cliente", flex: 1.2, minWidth: 285 },
    { field: "referente_payroll1", headerName: "Referente payroll 1", flex: 0.7, minWidth: 180 },
    { field: "nota_informativa", headerName: "Nota", width: 170, sortable: false,
      renderCell: ({ row }) => {
        if (!row.nota_informativa && !isAdmin) return null;
        const label = row.nota_informativa || "Aggiungi nota";
        return <button type="button"
          title={row.nota_informativa || "Aggiungi nota informativa"}
          aria-label={`${row.nota_informativa ? (isAdmin ? "Modifica" : "Leggi") : "Aggiungi"} nota informativa di ${row.ragione_sociale}`}
          onClick={() => setNotaInformativa({ codice: row.codice_univoco_azienda, cliente: row.ragione_sociale, testo: row.nota_informativa || "" })}
          className={`flex max-w-full items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold transition ${row.nota_informativa ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : "border border-dashed border-slate-300 text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"}`}>
          <MdNotes className="shrink-0" size={16} />
          <span className="truncate">{label}</span>
        </button>;
      } },
  ];
  const mensilitaColumn = tipoAggiuntivo ? {
      field: "mensilita_aggiuntiva",
      headerName: tipoAggiuntivo === "quattordicesima" ? "14ª mensilità" : "13ª mensilità",
      width: sottotab === "bpoint" ? 150 : 145, type: "number", sortable: true, align: "center", headerAlign: "center",
      sortComparator: (a, b) => Number(a ?? 0) - Number(b ?? 0),
      renderCell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="flex w-7 shrink-0 items-center justify-end gap-1">
            {row.tipologia_fatturazione !== "B" && Number(row.nota_mensilita_1) === 1 && <NoteDot title={row.titolo_nota_1 || defaultNoteTypes.nota_1.titolo}
              text={row.testo_nota_1 || defaultNoteTypes.nota_1.testo} className="h-3 w-3 cursor-help rounded-full border border-amber-600 bg-amber-400 shadow-sm" />}
            {row.tipologia_fatturazione !== "B" && Number(row.nota_mensilita_2) === 1 && <NoteDot title={row.titolo_nota_2 || defaultNoteTypes.nota_2.titolo}
              text={row.testo_nota_2 || defaultNoteTypes.nota_2.testo} className="h-3 w-3 cursor-help rounded-full border border-violet-600 bg-violet-500 shadow-sm" />}
          </span>
          <input key={`${row.codice_univoco_azienda}-${row.mensilita_aggiuntiva}`} type="number" min="0" step="1"
            defaultValue={row.mensilita_aggiuntiva == null || row.mensilita_aggiuntiva === "" ? "" : Math.round(Number(row.mensilita_aggiuntiva))}
            disabled={isRowClosed(row) || savingCode === row.codice_univoco_azienda}
            onFocus={selectZeroOnFocus} onBlur={(event) => saveAggiuntiva(row, event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }}
            className={`w-24 rounded border px-2 py-1 text-right font-semibold disabled:opacity-50 disabled:bg-gray-100 ${row.tipologia_fatturazione === "B" && Number(row.bpoint_427_escludi_mensilita_aggiuntive) === 1 ? "border-red-500 bg-red-100 text-red-900" : "border-amber-300 bg-amber-50 text-amber-800"}`} />
        </div>
      ),
    } : null;
  const columnsZI = [tipoColumn, ...commonColumns,
    editableNumberColumn("cedolini_ordinari", "Cedolini ordinari", 320),
    editableNumberColumn("cedolini_intermittenti", "Intermittenti", 320),
    editableNumberColumn("cedolini_somministrati", "Somministrati", 320),
    editableNumberColumn("cedolini_espatriati", "Espatriati iiPay", 320),
    { field: "stato_fondi", headerName: "Numero fondi", width: 320, type: "singleSelect", sortable: true, filterable: true,
      align: "center", headerAlign: "center", valueOptions: ["Obbl.", "Compilato"],
      valueGetter: ({ row }) => row.fondi_obbligatori && mostraFondiSync && row.numero_fondi_sync != null ? "Compilato" : row.stato_fondi,
      renderCell: ({ row }) => {
        if (!row.fondi_obbligatori) return null;
        const daSync = mostraFondiSync && row.numero_fondi_sync != null;
        if (daSync) return editableNumberColumn("numero_fondi_sync", "Numero fondi", 320).renderCell({ row });
        const numeroFondi = row.numero_fondi;
        const mancante = numeroFondi == null;
        return <div className="relative h-full w-full">
          <input key={`${row.codice_univoco_azienda}-fondi-${daSync ? "sync" : "manuale"}-${numeroFondi}`} type="number" min="0" step="1"
            defaultValue={numeroFondi == null ? "" : Number(numeroFondi)}
            disabled={isRowClosed(row) || savingCode === `${row.codice_univoco_azienda}-fondi`}
            onFocus={selectZeroOnFocus}
            onBlur={(event) => saveFondi(row, event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }}
            aria-required={row.fondi_obbligatori ? "true" : "false"}
            aria-label={`Numero fondi ${row.ragione_sociale}`}
            className={`absolute left-1/2 top-1/2 w-20 -translate-x-1/2 -translate-y-1/2 rounded border px-2 py-1 text-right font-semibold disabled:bg-gray-100 disabled:opacity-50 ${mancante ? "border-red-500 bg-red-50 text-red-800" : "border-blue-300 bg-blue-50 text-blue-800"}`} />
          <span className={`absolute left-[calc(50%+48px)] top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] font-bold uppercase ${mancante ? "text-red-600" : "text-green-700"}`}>
            {mancante ? "Obbl." : "Compilato"}
          </span>
        </div>;
      } },
    ...(mensilitaColumn ? [mensilitaColumn] : []),
  ];
  const columnsBpoint = [tipoColumn, ...commonColumns,
    editableNumberColumn("cedolini_ordinari", "Cedolini ordinari", 260),
    editableNumberColumn("cedolini_dirigenti", "Dirigenti", 260),
    editableNumberColumn("cedolini_edili", "Edili", 260),
    ...(mensilitaColumn ? [mensilitaColumn] : []),
  ];

  const Toolbar = () => (
    <GridToolbarContainer className="flex justify-between border-b px-2 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <GridToolbarQuickFilter placeholder="Cerca cliente o referente..." />
        {showTuttiTeam && (
          <button type="button" onClick={() => onTuttiTeamChange?.(!tuttiTeam)}
            className={`rounded border px-2.5 py-0.5 text-xs font-semibold transition-colors ${tuttiTeam ? "border-blue-600 bg-blue-600 text-white" : "border-blue-300 bg-white text-blue-700"}`}>
            TUTTI
          </button>
        )}
        {sottotab === "zi" && <div className="flex items-center gap-1">
          {[
            { id: "Z", label: "Z", active: "bg-blue-100 text-blue-700 border-blue-400 ring-1 ring-blue-300", idle: "bg-white text-gray-500 border-gray-200 hover:bg-gray-50" },
            { id: "I", label: "I", active: "bg-green-100 text-green-700 border-green-400 ring-1 ring-green-300", idle: "bg-white text-gray-500 border-gray-200 hover:bg-gray-50" },
          ].map(({ id, label, active, idle }) => (
            <button key={id} type="button"
              onClick={() => setTipologieFiltro((current) => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; })}
              className={`rounded border px-2.5 py-0.5 text-xs font-semibold transition-colors ${tipologieFiltro.has(id) ? active : idle}`}>
              {label}
            </button>
          ))}
        </div>}
      </div>
      <div className="flex border-b border-slate-200">
        {[{ id: "zi", label: "Zucchetti / iiPay" }, { id: "bpoint", label: "BPoint" }].map((item) => (
          <button key={item.id} type="button" onClick={() => { setSottotab(item.id); setTipologieFiltro(new Set()); }}
            className={`border-b-2 px-5 py-2.5 text-sm font-semibold transition ${sottotab === item.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {item.label}
          </button>
        ))}
      </div>
    </GridToolbarContainer>
  );

  const meseDatiLabel = periodoDati
    ? new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" })
      .format(new Date(periodoDati.anno, periodoDati.mese - 1, 1))
    : "";
  return (
    <div className="el-container space-y-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><div className="flex flex-wrap items-center gap-2"><h1 className="text-xl font-semibold text-gray-800">Cedolini</h1>
          <BillingClosureBadges state={billingClosureState(periodo, "cedolini")} /></div>
          <p className="text-sm text-gray-500">
            Riepilogo operativo BPoint (sez. 0), Zucchetti (sez. 1) e iiPay (sez. 2). Dati cedolini: <strong>{meseDatiLabel || "—"}</strong>.
          </p></div>
        {tipoAggiuntivo && <div className="flex items-center gap-2">
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Compila manualmente i cedolini di {tipoAggiuntivo === "quattordicesima" ? "14ª" : "13ª"} mensilità e premi Invio o esci dal campo.
          </div>
          <button type="button" onClick={openLineeGuida} className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100">
            Linee guida
          </button>
          {isAdmin && sottotab !== "bpoint" && <button type="button" onClick={openGestioneNote} className="rounded border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50">
            Gestisci note clienti
          </button>}
        </div>}
      </div>
      <div className="flex justify-end">
        <div className="flex flex-wrap items-center gap-5">
        {rows.some((row) => Number(row.cessato) === 1) && <button type="button"
          onClick={() => setSoloCessati((value) => !value)}
          className={`rounded border border-blue-200 px-3 py-1.5 text-sm text-blue-700 ${soloCessati ? "bg-blue-100 font-semibold" : "bg-blue-50"}`}>
          {soloCessati ? "Mostra tutti" : "Mostra cessati"}
        </button>}
        <button type="button" onClick={() => setSoloForzati((value) => !value)}
          className={`rounded border border-blue-200 px-3 py-1.5 text-sm text-blue-700 ${soloForzati ? "bg-blue-100 font-semibold" : "bg-blue-50"}`}>
          {soloForzati ? "Mostra tutti" : "Mostra forzati e bozze"}
        </button></div>
      </div>
      <div className={`grid gap-3 sm:grid-cols-2 ${sottotab === "bpoint" ? (tipoAggiuntivo ? "xl:grid-cols-5" : "xl:grid-cols-4") : (tipoAggiuntivo ? "xl:grid-cols-6" : "xl:grid-cols-5")}`}>
        <Metric label="Totale cedolini" value={totals.totale} zucchetti={totals.totale_z} iipay={totals.totale_i} bpoint={totals.totale_b} soloBpoint={sottotab === "bpoint"}
          modifiedValue={totals.totale_modificato} modifiedZucchetti={totals.totale_z_modificato} modifiedIipay={totals.totale_i_modificato} modifiedBpoint={totals.totale_b_modificato}
          tone="border-slate-300 bg-slate-100 text-slate-900" />
        <Metric label="Ordinari" value={totals.ordinari} zucchetti={totals.ordinari_z} iipay={totals.ordinari_i} bpoint={totals.ordinari_b} soloBpoint={sottotab === "bpoint"}
          modifiedValue={totals.ordinari_modificato} modifiedZucchetti={totals.ordinari_z_modificato} modifiedIipay={totals.ordinari_i_modificato} modifiedBpoint={totals.ordinari_b_modificato}
          tone="border-blue-200 bg-blue-50 text-blue-800" />
        {sottotab === "bpoint" && <Metric label="Dirigenti" value={totals.dirigenti} zucchetti={totals.dirigenti_z} iipay={totals.dirigenti_i} bpoint={totals.dirigenti_b} soloBpoint
          modifiedValue={totals.dirigenti_modificato} modifiedZucchetti={totals.dirigenti_z_modificato} modifiedIipay={totals.dirigenti_i_modificato} modifiedBpoint={totals.dirigenti_b_modificato}
          tone="border-purple-200 bg-purple-50 text-purple-800" />}
        {sottotab === "bpoint" && <Metric label="Edili" value={totals.edili} zucchetti={totals.edili_z} iipay={totals.edili_i} bpoint={totals.edili_b} soloBpoint
          modifiedValue={totals.edili_modificato} modifiedZucchetti={totals.edili_z_modificato} modifiedIipay={totals.edili_i_modificato} modifiedBpoint={totals.edili_b_modificato}
          tone="border-orange-200 bg-orange-50 text-orange-800" />}
        {sottotab === "zi" && <Metric label="Intermittenti" value={totals.intermittenti} zucchetti={totals.intermittenti_z} iipay={totals.intermittenti_i} bpoint={totals.intermittenti_b}
          modifiedValue={totals.intermittenti_modificato} modifiedZucchetti={totals.intermittenti_z_modificato} modifiedIipay={totals.intermittenti_i_modificato} modifiedBpoint={totals.intermittenti_b_modificato}
          tone="border-violet-200 bg-violet-50 text-violet-800" />}
        {sottotab === "zi" && <Metric label="Somministrati" value={totals.somministrati} zucchetti={totals.somministrati_z} iipay={totals.somministrati_i} bpoint={totals.somministrati_b}
          modifiedValue={totals.somministrati_modificato} modifiedZucchetti={totals.somministrati_z_modificato} modifiedIipay={totals.somministrati_i_modificato} modifiedBpoint={totals.somministrati_b_modificato}
          tone="border-cyan-200 bg-cyan-50 text-cyan-800" />}
        {sottotab === "zi" && <Metric label="Espatriati" value={totals.espatriati} zucchetti={totals.espatriati_z} iipay={totals.espatriati_i} bpoint={totals.espatriati_b}
          modifiedValue={totals.espatriati_modificato} modifiedZucchetti={totals.espatriati_z_modificato} modifiedIipay={totals.espatriati_i_modificato} modifiedBpoint={totals.espatriati_b_modificato}
          tone="border-emerald-200 bg-emerald-50 text-emerald-800" />}
        {tipoAggiuntivo && <Metric label={tipoAggiuntivo === "quattordicesima" ? "14ª mensilità" : "13ª mensilità"} value={totals.aggiuntiva} zucchetti={totals.aggiuntiva_z} iipay={totals.aggiuntiva_i} bpoint={totals.aggiuntiva_b} soloBpoint={sottotab === "bpoint"} tone="border-amber-200 bg-amber-50 text-amber-800" />}
      </div>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white"
        style={{ height: "calc(100vh - 140px)", minHeight: 620 }}>
        <DataGridPro rows={rowsPerTipologia.filter((row) =>
          (!soloCessati || Number(row.cessato) === 1)
          && (!soloForzati || row.ha_forzature)
        )}
          columns={sottotab === "bpoint" ? columnsBpoint : columnsZI} getRowId={(row) => row.codice_univoco_azienda}
          getRowClassName={({ row }) => Number(row.cessato) === 1 ? "cedolini-row-cessato" : ""}
          loading={loading} density="compact" disableRowSelectionOnClick
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          slots={{ toolbar: Toolbar, footer: CustomFooterPersonalized, loadingOverlay: LinearProgress }}
          slotProps={{ footer: { fileName: `cedolini_${anno}_${String(mese).padStart(2, "0")}` } }} />
      </div>
      <style>{`.cedolini-row-cessato { background-color: #fff7df !important; color: #8a4b08; }
        .cedolini-row-cessato:hover { background-color: #ffefbd !important; }`}</style>
      {notaInformativa && <div className="fixed inset-0 z-[320] flex items-center justify-center bg-slate-900/40 p-4">
        <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="nota-informativa-title">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <div>
              <h2 id="nota-informativa-title" className="text-lg font-semibold text-slate-800">Nota informativa</h2>
              <p className="mt-0.5 text-sm text-slate-500">{notaInformativa.cliente}</p>
            </div>
            <button type="button" onClick={() => setNotaInformativa(null)} className="rounded p-1 text-2xl text-slate-400 hover:bg-slate-100">×</button>
          </div>
          <div className="p-5">
            {isAdmin ? <>
              <textarea autoFocus rows={6} value={notaInformativa.testo}
                onChange={(event) => setNotaInformativa((current) => ({ ...current, testo: event.target.value }))}
                placeholder="Inserisci una nota informativa per il cliente..."
                className="w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm leading-6 outline-none focus:border-blue-400" />
              <p className="mt-2 text-xs text-slate-500">Lascia il testo vuoto e salva per eliminare la nota.</p>
            </> : <div className="whitespace-pre-wrap rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-slate-800">
              {notaInformativa.testo}
            </div>}
          </div>
          <div className="flex justify-end gap-2 border-t px-5 py-4">
            <button type="button" onClick={() => setNotaInformativa(null)} disabled={notaInformativaSaving}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 disabled:opacity-50">{isAdmin ? "Annulla" : "Chiudi"}</button>
            {isAdmin && <button type="button" onClick={saveNotaInformativa} disabled={notaInformativaSaving}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {notaInformativaSaving ? "Salvataggio..." : "Salva"}
            </button>}
          </div>
        </div>
      </div>}
      {lineeGuidaOpen && <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-900/40 p-4">
        <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl" role="dialog" aria-modal="true">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h2 className="text-lg font-semibold text-slate-800">Linee guida {tipoAggiuntivo === "quattordicesima" ? "14ª" : "13ª"} mensilità</h2>
            <button type="button" onClick={() => setLineeGuidaOpen(false)} className="rounded p-1 text-2xl text-slate-400 hover:bg-slate-100">×</button>
          </div>
          <div className="max-h-[72vh] overflow-y-auto p-5">
            {lineeGuidaLoading ? <div className="min-h-48 rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Caricamento...</div> : sottotab === "bpoint" ?
              (isAdmin ? <textarea value={lineeGuida} onChange={(event) => setLineeGuida(event.target.value)} rows={8}
                placeholder="Inserisci una sola nota operativa per la mensilità aggiuntiva BPoint..."
                className="w-full resize-y rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm leading-6 text-slate-800 outline-none focus:border-purple-400" />
                : <div className="min-h-32 whitespace-pre-wrap rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm leading-6 text-slate-800">{lineeGuida || "Nessuna nota inserita."}</div>)
              : <div className="space-y-4">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full border border-amber-600 bg-amber-400" />
                  <h3 className="text-sm font-semibold text-amber-900">{noteTypes.nota_1?.titolo || defaultNoteTypes.nota_1.titolo}</h3>
                </div>
                <div className="text-sm font-medium leading-6 text-amber-900 whitespace-pre-wrap">{noteTypes.nota_1?.testo || defaultNoteTypes.nota_1.testo}</div>
                <div className="mt-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-900/70">Spiegazione</div>
                  {isAdmin && !lineeGuidaPreview ? <textarea
                    value={noteTypes.nota_1?.descrizione || ""}
                    onChange={(event) => updateNoteType("nota_1", "descrizione", event.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-amber-400"
                    placeholder="Spiegazione operativa della nota 1..."
                  /> : <div className="min-h-16 rounded-lg border border-amber-200 bg-white/80 px-3 py-2 text-sm leading-6 text-slate-700 whitespace-pre-wrap">{noteTypes.nota_1?.descrizione || "Nessuna spiegazione inserita."}</div>}
                </div>
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full border border-violet-600 bg-violet-500" />
                  <h3 className="text-sm font-semibold text-violet-900">{noteTypes.nota_2?.titolo || defaultNoteTypes.nota_2.titolo}</h3>
                </div>
                <div className="text-sm font-medium leading-6 text-violet-900 whitespace-pre-wrap">{noteTypes.nota_2?.testo || defaultNoteTypes.nota_2.testo}</div>
                <div className="mt-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-900/70">Spiegazione</div>
                  {isAdmin && !lineeGuidaPreview ? <textarea
                    value={noteTypes.nota_2?.descrizione || ""}
                    onChange={(event) => updateNoteType("nota_2", "descrizione", event.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-violet-400"
                    placeholder="Spiegazione operativa della nota 2..."
                  /> : <div className="min-h-16 rounded-lg border border-violet-200 bg-white/80 px-3 py-2 text-sm leading-6 text-slate-700 whitespace-pre-wrap">{noteTypes.nota_2?.descrizione || "Nessuna spiegazione inserita."}</div>}
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-800">Altre note</h3>
                  <div className="flex items-center gap-2">
                    {isAdmin && <button type="button" onClick={() => setLineeGuidaPreview((current) => !current)} className={`rounded border px-3 py-1.5 text-xs font-semibold ${lineeGuidaPreview ? "border-slate-300 bg-slate-100 text-slate-800" : "border-blue-200 bg-white text-blue-700 hover:bg-blue-50"}`}>
                      {lineeGuidaPreview ? "Torna in modifica" : "Anteprima"}
                    </button>}
                    {isAdmin && <button type="button" onClick={openGestioneNote} className="rounded border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                      Gestisci nota 1 / nota 2
                    </button>}
                  </div>
                </div>
                {isAdmin && !lineeGuidaPreview ? <RichTextEditor value={lineeGuida} onChange={setLineeGuida} disabled={lineeGuidaLoading} />
                  : lineeGuida ? <div className="min-h-48 rounded-lg bg-slate-50 p-4 text-sm leading-6 text-slate-700"
                      dangerouslySetInnerHTML={{ __html: asFormattedHtml(lineeGuida) }} />
                    : <div className="min-h-48 rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Nessuna nota aggiuntiva inserita.</div>}
              </div>
            </div>}
          </div>
          <div className="flex justify-end gap-2 border-t px-5 py-4">
            <button type="button" onClick={() => setLineeGuidaOpen(false)} className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700">Chiudi</button>
            {isAdmin && <button type="button" onClick={saveLineeGuida} disabled={lineeGuidaLoading} className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Salva</button>}
          </div>
        </div>
      </div>}
      {noteOpen && <div className="fixed inset-0 z-[310] flex items-center justify-center bg-slate-900/40 p-4">
        <div className="flex max-h-[92vh] w-full max-w-6xl flex-col rounded-xl bg-white shadow-2xl" role="dialog" aria-modal="true">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Gestione note 13a / 14a mensilita</h2>
              <p className="text-sm text-slate-500">Modifica i testi standard e assegna le note anche in modo massivo ai clienti.</p>
            </div>
            <button type="button" onClick={() => setNoteOpen(false)} className="rounded p-1 text-2xl text-slate-400 hover:bg-slate-100">×</button>
          </div>
          <div className="grid gap-5 overflow-y-auto p-5 lg:grid-cols-[360px,minmax(0,1fr)]">
            <div className="space-y-4">
              {["nota_1", "nota_2"].map((tipo, index) => (
                <div key={tipo} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <span className={`h-3 w-3 rounded-full border ${index === 0 ? "border-amber-600 bg-amber-400" : "border-violet-600 bg-violet-500"}`} />
                    <span className="text-sm font-semibold text-slate-800">{tipo === "nota_1" ? "Nota 1" : "Nota 2"}</span>
                  </div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Titolo</label>
                  <input type="text" value={noteTypes[tipo]?.titolo || ""} onChange={(event) => updateNoteType(tipo, "titolo", event.target.value)}
                    className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-400" />
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Testo</label>
                  <textarea value={noteTypes[tipo]?.testo || ""} onChange={(event) => updateNoteType(tipo, "testo", event.target.value)}
                    rows={4} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-400" />
                </div>
              ))}
            </div>
            <div className="min-w-0 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <input type="text" value={noteSearch} onChange={(event) => setNoteSearch(event.target.value)}
                  placeholder="Cerca per cliente o codice..."
                  className="min-w-[260px] flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-400" />
                <button type="button" onClick={() => applyMassNote("nota_1", true)}
                  className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 disabled:opacity-50" disabled={!selectedCodes.length}>
                  Assegna nota 1
                </button>
                <button type="button" onClick={() => applyMassNote("nota_2", true)}
                  className="rounded border border-violet-300 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-800 disabled:opacity-50" disabled={!selectedCodes.length}>
                  Assegna nota 2
                </button>
                <button type="button" onClick={clearSelectedNotes}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50" disabled={!selectedCodes.length}>
                  Rimuovi note
                </button>
              </div>
              <div className="rounded-xl border border-slate-200">
                <div className="grid grid-cols-[44px,80px,minmax(0,1fr),120px,90px,90px] items-center gap-3 border-b bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <label className="flex items-center justify-center"><input type="checkbox"
                    checked={filteredNoteRows.length > 0 && filteredNoteRows.every((row) => selectedClients[row.codice_univoco_azienda])}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setSelectedClients((current) => {
                        const next = { ...current };
                        filteredNoteRows.forEach((row) => { next[row.codice_univoco_azienda] = checked; });
                        return next;
                      });
                    }} /></label>
                  <span>Tipo</span>
                  <span>Cliente</span>
                  <span>Codice</span>
                  <span>Nota 1</span>
                  <span>Nota 2</span>
                </div>
                <div className="max-h-[52vh] overflow-y-auto">
                  {noteLoading ? <div className="p-6 text-sm text-slate-500">Caricamento...</div> : filteredNoteRows.map((row) => (
                    <div key={row.codice_univoco_azienda} className="grid grid-cols-[44px,80px,minmax(0,1fr),120px,90px,90px] items-center gap-3 border-b px-4 py-3 text-sm last:border-b-0">
                      <label className="flex items-center justify-center"><input type="checkbox" checked={Boolean(selectedClients[row.codice_univoco_azienda])} onChange={() => toggleClientSelection(row.codice_univoco_azienda)} /></label>
                      <span className={`inline-flex w-fit rounded px-2 py-0.5 text-xs font-bold ${row.tipologia_fatturazione === "I" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{row.tipologia_fatturazione}</span>
                      <span className="truncate" title={row.ragione_sociale}>{row.ragione_sociale}</span>
                      <span className="text-slate-500">{row.codice_univoco_azienda}</span>
                      <label className="flex items-center justify-center"><input type="checkbox" checked={Boolean(row.nota_1)} onChange={(event) => setClientNote(row.codice_univoco_azienda, "nota_1", event.target.checked)} /></label>
                      <label className="flex items-center justify-center"><input type="checkbox" checked={Boolean(row.nota_2)} onChange={(event) => setClientNote(row.codice_univoco_azienda, "nota_2", event.target.checked)} /></label>
                    </div>
                  ))}
                  {!noteLoading && filteredNoteRows.length === 0 && <div className="p-6 text-sm text-slate-500">Nessun cliente trovato.</div>}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t px-5 py-4">
            <button type="button" onClick={() => setNoteOpen(false)} className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700">Chiudi</button>
            <button type="button" onClick={saveNoteConfiguration} disabled={noteSaving || noteLoading}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              Salva
            </button>
          </div>
        </div>
      </div>}
    </div>
  );
};
